// Genuine ESM, shared between src/App.jsx (Vite/browser, native import) and functions/index.js
// (Node/Cloud Functions, CommonJS, loaded via dynamic import()) — same interop pattern as
// shared/practiceGroupMatching.mjs and shared/uniformAvailability.mjs (see either file's header for
// why this must be .mjs rather than .cjs: Vite's dev server doesn't apply CJS interop to local
// files, only `vite build` does).
//
// Single source of truth for the roster-builder gear/duty item catalog. Before this module existed,
// functions/index.js's email-template builders had their own inline `gearItem === "Ball Bucket" ?
// icon-a : icon-b` ternary — correct only as long as exactly two items ever existed, and silently
// wrong the moment a third (Team Sheet) was added (it would render every non-"Ball Bucket" item,
// including Team Sheet, with the Bownet net emoji). Consolidating the catalog here means adding a
// future 4th item is a one-line change in exactly one place, not a hunt through every hardcoded
// ternary/2-column assumption across both the client and functions codebases.
//
// `requiresCustody` distinguishes items that go through the ORG/OWN custody-declare and
// RETURN GEAR flow (Ball Bucket, Bownet — physical equipment that has to come back) from items that
// don't (Team Sheet — a duty/responsibility assignment, nothing to check out or return). Assignment
// itself (handleGearAssign, the gear_assigned notification, gearHistory season-fairness tracking) is
// identical for every item regardless of this flag; only the custody-declare/return UI and the
// post-tournament return reminder branch on it.
export const GEAR_ITEMS = [
  {id: "ball_bucket", label: "Ball Bucket", icon: "🪣", requiresCustody: true},
  {id: "bownet", label: "Bownet", icon: "🥅", requiresCustody: true},
  {id: "team_sheet", label: "Team Sheet", icon: "📋", requiresCustody: false},
];

export function gearIcon(label) {
  return GEAR_ITEMS.find((g) => g.label === label)?.icon || "⚙";
}

// Defaults to true (custody required) for an unrecognized label — the existing two items' behavior
// (ORG/OWN declare + return flow) is the historical default this app has always had; only an item
// explicitly marked requiresCustody:false opts out of it.
export function gearRequiresCustody(label) {
  const item = GEAR_ITEMS.find((g) => g.label === label);
  return item ? item.requiresCustody !== false : true;
}

// Per-node enablement for the 3 gear/duty items — see the FEATURE FLAGS panel (App.jsx) for how
// these get set. Absence of any configuration (no perNode object, no entry for this node, no entry
// for this item) defaults to ENABLED — a brand-new node or a node nobody has touched in the Feature
// Flags panel behaves exactly like today (every item available), matching the "off is an explicit,
// deliberate action" posture used elsewhere in this app (e.g. a missing coach nodeId defaults to
// root rather than defaulting to "broken"). Only an explicit `false` disables an item for a node.
export function isGearItemEnabledForNode(featureFlags, nodeId, itemId) {
  return featureFlags?.perNode?.[nodeId]?.[itemId] !== false;
}
